import type { NpcEncounter, NpcFaction, NpcHullTier, NpcUnit, StarSystem } from "./types";
import { RICHNESS_MULTIPLIER, resourceCrPerM3, type Richness } from "./resourceCatalog";
import {
  computeMissionDifficulty,
  type MissionDifficultyResult,
  type PlayerCombatStats
} from "./missionDifficulty";

// Mining missions are T1/T2 only, by design -- high-tier miners consume
// their own mined resources directly instead of running the mission-board
// version (CLAUDE.md; design doc's "Mining Missions -- Removed for T3/T4"
// revision, added after 16.29).
export type MiningHullTier = "T1" | "T2";

export type MiningBand = "Easy" | "Challenge" | "Very Hard" | "Almost Impossible";
export type MiningThreatKind = "Micro" | "Raider" | "Swarm" | "Duelist" | "Elite";

export interface MiningMission {
  band: MiningBand;
  threatKind: MiningThreatKind | null;
  resource: string;
  richness: Richness;
  quotaM3: number;
  threat: NpcEncounter | null;
  threatDifficulty: MissionDifficultyResult | null;
  rewardCr: number;
}

interface BandDefinition {
  band: MiningBand;
  richness: Richness;
  threatKind: MiningThreatKind | null;
  quotaFraction: number;
}

// The 6 canonical mining-mission rows (16.24 point 3/4): quota as a fraction
// of the ship's own cargo capacity -- Easy 25%, Challenge 45-60% (resolved
// here into 3 fixed sub-fractions by attached-threat severity, reproducing
// the design doc's own worked examples), Very Hard 85%, Almost Impossible
// 100%. Attached threat per band per 16.24 point 4: none / Micro-Raider-
// Swarm / a solo Alien Duelist / a solo Alien Elite.
const BAND_DEFINITIONS: BandDefinition[] = [
  { band: "Easy", richness: "Standard", threatKind: null, quotaFraction: 0.25 },
  { band: "Challenge", richness: "Rich", threatKind: "Micro", quotaFraction: 0.45 },
  { band: "Challenge", richness: "Rich", threatKind: "Raider", quotaFraction: 0.55 },
  { band: "Challenge", richness: "Rich", threatKind: "Swarm", quotaFraction: 0.6 },
  { band: "Very Hard", richness: "Exceptional", threatKind: "Duelist", quotaFraction: 0.85 },
  { band: "Almost Impossible", richness: "Exceptional", threatKind: "Elite", quotaFraction: 1.0 }
];

const THREAT_FACTION: Record<MiningThreatKind, NpcFaction> = {
  Micro: "Pirate",
  Raider: "Pirate",
  Swarm: "Pirate",
  Duelist: "Alien",
  Elite: "Alien"
};

// Which specific encounter stands in for "a Micro/Raider/Duelist/Elite" or
// "a small Swarm" isn't itself specified in 16.24 beyond the role name --
// this picks the first roster match (encounter-ID order) for the target
// faction/tier: the sole matching Solo unit for Micro/Raider/Duelist/Elite,
// or the first Swarm-structure encounter for "a small Swarm." Reproduces
// the design doc's own worked examples exactly (PT1-002/PT1-001/PT1-008/
// AT1-001/AT1-004 and their T2 equivalents).
function findThreat(
  kind: MiningThreatKind,
  hullTier: NpcHullTier,
  encounters: NpcEncounter[]
): NpcEncounter {
  const faction = THREAT_FACTION[kind];

  if (kind === "Swarm") {
    const found = encounters.find(
      (e) => e.faction === faction && e.hullTier === hullTier && e.structure === "Swarm"
    );
    if (!found) {
      throw new Error(`No Swarm encounter found for ${faction} ${hullTier}`);
    }
    return found;
  }

  const found = encounters.find((e) => {
    if (e.faction !== faction || e.hullTier !== hullTier || e.structure !== "Solo") {
      return false;
    }
    const composition = e.composition.replace(/\s*\([^)]*\)\s*$/, "");
    return composition === `1x ${kind}`;
  });
  if (!found) {
    throw new Error(`No Solo ${kind} encounter found for ${faction} ${hullTier}`);
  }
  return found;
}

function assertMiningHullTier(hullTier: string): asserts hullTier is MiningHullTier {
  if (hullTier !== "T1" && hullTier !== "T2") {
    throw new Error(
      `Mining missions don't exist for ${hullTier} -- T3/T4 miners consume mined ` +
        `resources directly instead of running the mission-board version (CLAUDE.md).`
    );
  }
}

// Generates every band's mission for a single resource (16.24 points 1-5):
// reward = quota x resource CR/m3 x richness + the attached threat's own
// existing reward value, reused directly -- no new pricing invented.
// `player` scores the attached threat through the Mission Difficulty Score
// engine (missionDifficulty.ts); pass whatever combat stats are relevant to
// "who'd actually be fighting this" (a Digger's own DPS is 0 -- 16.23's
// drone-vs-foe table, not this engine, answers solo-feasibility for miners).
export function generateMissionsForResource(
  resource: string,
  cargoM3: number,
  hullTier: MiningHullTier,
  encounters: NpcEncounter[],
  units: NpcUnit[],
  player: PlayerCombatStats
): MiningMission[] {
  assertMiningHullTier(hullTier);

  return BAND_DEFINITIONS.map((def) => {
    const quotaM3 = Math.round(def.quotaFraction * cargoM3);
    const threat = def.threatKind ? findThreat(def.threatKind, hullTier, encounters) : null;
    const threatDifficulty = threat ? computeMissionDifficulty(player, threat, units) : null;
    const nodeValue = quotaM3 * resourceCrPerM3(resource) * RICHNESS_MULTIPLIER[def.richness];
    const rewardCr = Math.round(nodeValue + (threat?.rewardCrEq ?? 0));

    return {
      band: def.band,
      threatKind: def.threatKind,
      resource,
      richness: def.richness,
      quotaM3,
      threat,
      threatDifficulty,
      rewardCr
    };
  });
}

// Every available mission for a system, across all of its 2-4 stocked
// resources (16.22). This is the full generated pool per 16.24's formula --
// see curateMiningMissionBoard() below for the 11-mission board selected
// out of this pool.
export function generateSystemMiningMissions(
  system: StarSystem,
  cargoM3: number,
  hullTier: MiningHullTier,
  encounters: NpcEncounter[],
  units: NpcUnit[],
  player: PlayerCombatStats
): MiningMission[] {
  return system.resources.flatMap((resource) =>
    generateMissionsForResource(resource, cargoM3, hullTier, encounters, units, player)
  );
}

export interface BoardSlot {
  band: MiningBand;
  threatKind: MiningThreatKind | null;
}

// The locked 11-mission board shape (16.24): 2 Easy, 6 Challenge (2 each of
// Micro/Raider/Swarm, i.e. the 45%/55%/60% sub-bands), 2 Very Hard, 1 Almost
// Impossible -- "matching the combat Mission Board's own curation shape."
export const BOARD_SLOTS: BoardSlot[] = [
  { band: "Easy", threatKind: null },
  { band: "Easy", threatKind: null },
  { band: "Challenge", threatKind: "Micro" },
  { band: "Challenge", threatKind: "Micro" },
  { band: "Challenge", threatKind: "Raider" },
  { band: "Challenge", threatKind: "Raider" },
  { band: "Challenge", threatKind: "Swarm" },
  { band: "Challenge", threatKind: "Swarm" },
  { band: "Very Hard", threatKind: "Duelist" },
  { band: "Very Hard", threatKind: "Duelist" },
  { band: "Almost Impossible", threatKind: "Elite" }
];

// Curates the 11-mission board out of generateSystemMiningMissions()'s full
// pool. Resources cycle round-robin by mission index (0-10, continuing
// across band boundaries rather than resetting per band) -- with a 4-
// resource system this reproduces 16.24's own worked example (system S008)
// resource-for-resource across all 11 rows.
export function curateMiningMissionBoard(
  missions: MiningMission[],
  resources: string[]
): MiningMission[] {
  if (resources.length === 0) {
    throw new Error("Cannot curate a mining mission board with no resources");
  }

  return BOARD_SLOTS.map((slot, index) => {
    const resource = resources[index % resources.length];
    const match = missions.find(
      (m) => m.band === slot.band && m.threatKind === slot.threatKind && m.resource === resource
    );
    if (!match) {
      throw new Error(
        `No mission found for board slot ${index} ` +
          `(${slot.band}/${slot.threatKind ?? "none"}, ${resource})`
      );
    }
    return match;
  });
}
